<template>
  <div id="adminTemplate">
    <div class="admin-header">
      <div style="display: inline-flex">
        <section>
          <b-tabs v-model="activeTab" position="is-left" class="block">
            <b-tab-item class="tab-option" :label="$t('admin_page.user')" />
            <b-tab-item
              class="tab-option"
              v-if="canSeeRolesTab"
              :label="$t('admin_page.role')"
            />
            <b-tab-item
              class="tab-option"
              :label="$t('admin_page.instrumentation')"
            />
            <b-tab-item
              class="tab-option"
              v-if="canSeeSitesTab"
              :label="$t('admin_page.sites')"
            />
            <b-tab-item
              class="tab-option"
              v-if="canManageInstrumentationTypes"
              :label="$t('admin_page.mte_types')"
            />
            <b-tab-item
              class="tab-option"
              v-if="canManageNotificationBanner"
              :label="$t('admin_page.notification_banner')"
            />
          </b-tabs>
        </section>
      </div>

      <div class="admin-right-section">
        <div v-if="!isTabActive('notification')" class="admin-search">
          <form v-on:submit.prevent="adminSearch">
            <b-field>
              <b-input
                :placeholder="$t('search')"
                type="search"
                icon="magnify"
                icon-clickable
                class="search-input"
                v-model="search"
                @icon-click="adminSearch"
              >
              </b-input>
            </b-field>
          </form>
        </div>
        <b-button
          @click="openAddRole"
          v-if="isTabActive('roles') && canCreateRoles"
          class="button-add-item"
          >{{ $t('admin_page.add_role') }}</b-button
        >
        <b-button
          @click="addInstrumentation"
          v-if="isTabActive('instrumentation') && canEditInstrumentation"
          class="button-add-item"
          >{{ $t('admin_page.add_instrumentation') }}</b-button
        >

        <b-button
          @click="openAddSite"
          v-if="isTabActive('sites') && canCreateSites"
          class="button-add-item"
          >{{ $t('admin_page.add_site') }}</b-button
        >
        <b-button
          @click="addMteType"
          v-if="isTabActive('mteTypes') && canManageInstrumentationTypes"
          class="button-add-item"
          style="width: 60%"
        >
          <div>{{ $t('admin_page.add_mte_type') }}</div>
        </b-button>
      </div>
    </div>

    <users :search="search" ref="users" v-if="isTabActive('users')" />
    <role-list
      :search="search"
      ref="roleList"
      v-if="isTabActive('roles') && canSeeRolesTab"
    />
    <instrumentations-list
      :search="search"
      ref="instrumentationsList"
      v-if="isTabActive('instrumentation')"
    />
    <site-list :search="search" ref="siteList" v-if="isTabActive('sites')" />
    <mtetype-list
      :search="search"
      ref="mteTypeList"
      v-if="isTabActive('mteTypes') && canManageInstrumentationTypes"
    />
    <announcement-banner :search="search" v-if="isTabActive('notification')" />
  </div>
</template>

<script>
import Users from './Users';
import RoleList from './RoleList';
import SiteList from './SiteList';
import MteTypeList from './MteTypeList';
import AnnouncementBanner from './AnnouncementBanner';
import Instrumentations from './Instrumentations';
import adminPageConstants from '../../constants/adminPageConstants';
import utilities_set_search_text from '../../store/utilities/actionTypes';

const ADMIN_ROLE = 'admin';
const ADMIN_USERS_SEARCH_STORAGE_KEY = 'admin_users_search_text';

export default {
  name: 'admin-template',
  components: {
    users: Users,
    'role-list': RoleList,
    'site-list': SiteList,
    'mtetype-list': MteTypeList,
    'announcement-banner': AnnouncementBanner,
    'instrumentations-list': Instrumentations,
  },

  data() {
    return {
      activeTab: 0,
    };
  },
  computed: {
    user() {
      return this.$store.state.currentUser;
    },
    canAddUser() {
      return this.user.role === ADMIN_ROLE;
    },
    canSeeRolesTab() {
      return this.$store.getters.hasPermission('admin.approvals.create_roles');
    },
    canSeeSitesTab() {
      // Admin access grants default read-only access to Sites tab
      return true;
    },
    canCreateSites() {
      return this.$store.getters.hasPermission('admin.site.create');
    },
    canCreateRoles() {
      return this.$store.getters.hasPermission('admin.approvals.create_roles');
    },
    canManageInstrumentationTypes() {
      return this.$store.getters.hasPermission(
        'admin.instrumentation.create_edit_types'
      );
    },
    canEditInstrumentation() {
      return this.$store.getters.hasAnyPermission([
        'admin.instrumentation.edit_primary',
        'admin.instrumentation.edit_all',
      ]);
    },
    canManageNotificationBanner() {
      return Boolean(this.$store.getters.isPowerAdmin);
    },
    tabOrder() {
      const order = ['users'];

      if (this.canSeeRolesTab) {
        order.push('roles');
      }

      order.push('instrumentation');

      if (this.canSeeSitesTab) {
        order.push('sites');
      }

      if (this.canManageInstrumentationTypes) {
        order.push('mteTypes');
      }

      if (this.canManageNotificationBanner) {
        order.push('notification');
      }

      return order;
    },
    tabIndexes() {
      return this.tabOrder.reduce((acc, tabName, index) => {
        acc[tabName] = index;
        return acc;
      }, {});
    },
    search: {
      get() {
        return this.$store.state.UtilitiesModule.search_text;
      },
      set(text) {
        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem(
            ADMIN_USERS_SEARCH_STORAGE_KEY,
            text || ''
          );
        }
        this.$store.commit(utilities_set_search_text, {
          search_text: text,
        });
      },
    },
  },
  watch: {
    user: {
      async handler(val) {
        if (val && Object.keys(val).length > 0) {
          await this.adminSearch();
        }
      },
    },
    activeTab: {
      handler() {
        if (this.activeTab < 0 || this.activeTab >= this.tabOrder.length) {
          this.activeTab = 0;
          return;
        }

        // timeout needed because activeTab is
        // changed before the component renders
        setTimeout(async () => {
          await this.adminSearch();
        }, 500);
      },
    },
  },
  async created() {
    const persistedSearch =
      typeof window !== 'undefined'
        ? window.sessionStorage.getItem(ADMIN_USERS_SEARCH_STORAGE_KEY)
        : null;

    if (
      persistedSearch !== null &&
      this.$store.state.UtilitiesModule.search_text !== persistedSearch
    ) {
      this.$store.commit(utilities_set_search_text, {
        search_text: persistedSearch,
      });
    }

    const savedSelectedTab = this.$cookies.get(
      adminPageConstants.adminCookies.admin_selected_tab
    );
    if (savedSelectedTab != null) {
      const parsedTab = parseInt(savedSelectedTab, 10);
      this.activeTab =
        parsedTab >= 0 && parsedTab < this.tabOrder.length ? parsedTab : 0;
    }
    this.$cookies.remove(adminPageConstants.adminCookies.admin_selected_tab);
  },
  async mounted() {
    this.adminSearch();
  },
  methods: {
    isTabActive(tabName) {
      return this.activeTab === this.tabIndexes[tabName];
    },
    addInstrumentation() {
      this.$refs.instrumentationsList.openAddInstrumentation();
    },
    openAddRole() {
      this.$cookies.set(
        adminPageConstants.adminCookies.admin_selected_tab,
        this.activeTab
      );
      this.$router.push({
        name: 'create-role',
      });
    },
    // openAddUser() {
    //   this.$cookies.set(
    //     adminPageConstants.adminCookies.admin_selected_tab,
    //     this.activeTab
    //   );
    //   this.$router.push({
    //     name: 'create-user',
    //   });
    // },
    openAddSite() {
      this.$cookies.set(
        adminPageConstants.adminCookies.admin_selected_tab,
        this.activeTab
      );
      this.$router.push({
        name: 'create-site',
      });
    },
    addMteType() {
      this.$cookies.set(
        adminPageConstants.adminCookies.admin_selected_tab,
        this.activeTab
      );
      this.$router.push({
        name: 'mtetype-create',
      });
    },
    async adminSearch() {
      const query = {
        search: this.search,
      };

      const activeTabName = this.tabOrder[this.activeTab];

      switch (activeTabName) {
        case 'users':
          // Users component now handles search automatically via watch
          break;
        case 'roles':
          if (this.$refs.roleList) {
            await this.$refs.roleList.getRoles(query);
          }
          break;
        case 'instrumentation':
          if (this.$refs.instrumentationsList) {
            await this.$refs.instrumentationsList.getInstrumentations(query);
          }
          break;
        case 'sites':
          if (this.$refs.siteList) {
            await this.$refs.siteList.getSites(query);
          }
          break;
        case 'mteTypes':
          if (this.$refs.mteTypeList) {
            await this.$refs.mteTypeList.getMteTypes(query);
          }
          break;
        default:
          break;
      }
      return true;
    },
  },
};
</script>

<style lang="scss">
.admin-header {
  font-family: 'Siemens Sans';
  display: inline-flex;
  justify-content: space-between;
  position: relative;
  width: 100%;
  height: 50px;
  background-color: #fafafa;
  font-size: 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  color: #333;
  padding-left: 20px;

  .admin-right-section {
    float: right;
    display: inline-flex;
    .admin-search {
      height: 100%;
      padding: 5px 20px;
      background-color: #e0e0e0;
      .admin-search-input input {
        border: 0px !important;
        background-color: #e0e0e0 !important;
        outline: none;
        box-shadow: none;
      }
    }

    .button-add-item {
      background-color: #4a86e8;
      color: white;
      height: 100%;
      width: 40%;

      &.disabled {
        background-color: lightgray;
        pointer-events: none;
        cursor: pointer;
      }
    }
  }
  .list-type {
    cursor: pointer;
    height: 50px;
    text-align: center;
    line-height: 50px;
    margin: 0 20px;
    min-width: 150px;
    float: left;
    &.active {
      border-bottom: 3px solid #4a86e8;
    }
  }

  .tabs li.is-active a {
    border-bottom-color: $siemens-logo-color;
  }

  .tabs a {
    border-bottom-width: 2px;
  }

  .b-tabs .tab-content {
    padding: 0;
  }
}
</style>
